import React from 'react';
import type { FC } from 'react';
import { match, isType } from 'variant';
import { Trans } from '@wix/cli-i18n';
import { DeploymentStatus } from '@wix/document-management-client';
import { useAsync, Spinner, Alert, Badge, Link, Box } from '@wix/cli-ui-kit';
import { ErrorViewer } from '@wix/cli-error-reporting';
import { useCreatePreview } from './useCreatePreview.js';

interface Props {
  force: boolean;
}

export const PreviewCommand: FC<Props> = ({ force }) => {
  const createPreview = useCreatePreview();

  const { status } = useAsync(async () => {
    const { previewUrl, deploymentStatus } = await createPreview();

    if (!force && isType(deploymentStatus, DeploymentStatus.Error)) {
      throw deploymentStatus.deploymentError;
    }

    return { previewUrl, deploymentStatus };
  }, []);

  return match(status, {
    Error: () => null,
    Loading: () => (
      <Spinner text={<Trans i18nKey="preview_command.loading" />} />
    ),
    Success: ({ result: { previewUrl, deploymentStatus } }) => (
      <>
        {isType(deploymentStatus, DeploymentStatus.Error) ? (
          <Box marginBottom={1} flexDirection="column">
            <ErrorViewer error={deploymentStatus.deploymentError} />
          </Box>
        ) : null}
        <Alert type="success">
          <Badge skin="success">
            <Trans i18nKey="general.success" />
          </Badge>{' '}
          <Trans
            i18nKey="preview_command.success"
            components={[<Link url={previewUrl} />]}
          />
        </Alert>
      </>
    ),
  });
};
